// StockTransfersMobile.jsx — onglet « Transferts de Stock » (mobile).
// Consultation + suppression. La création/édition (recherche produit, lignes
// éditables, totaux) reste sur desktop : voir StockTransfers dans GestionStock.jsx.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message, Modal } from "antd";
import {
  Summary, Segmented, EmptyState, Sheet, ActionRow, DetailRow, Note,
  Pill, CountChip, Mono, Ico, fmt,
} from "../produits/productMobileKit";
import {
  fetchTransfers, fetchTransferItems, deleteTransfer,
} from "./stockMobileApi";
import { PERM, fmtDate, fmtQty, transferPill } from "./stockMobileShared";

const PAGE = 20;

export default function StockTransfersMobile({ selectedWarehouse, hasPermission }) {
  const canDelete = hasPermission(PERM.transferDelete);

  const [viewMode, setViewMode] = useState("sent");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [detail, setDetail] = useState(null);   // transfert sélectionné
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  const load = useCallback(
    async (p) => {
      if (!selectedWarehouse) { setRows([]); setTotal(0); return; }
      setLoading(true);
      try {
        const { rows: incoming, total: t } = await fetchTransfers({
          warehouse: selectedWarehouse, viewType: viewMode, page: p, limit: PAGE,
        });
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(t);
      } catch {
        message.error("Impossible de charger les transferts de stock");
      } finally {
        setLoading(false);
      }
    },
    [selectedWarehouse, viewMode]
  );

  useEffect(() => { setPage(1); load(1); }, [load]);

  const totalQty = useMemo(
    () => rows.reduce((s, r) => s + (Number(r.total_quantity) || 0), 0),
    [rows]
  );

  const openDetail = async (t) => {
    setDetail(t);
    setItems([]);
    setItemsLoading(true);
    try {
      setItems(await fetchTransferItems(t.id));
    } catch {
      message.error("Impossible de charger les lignes du transfert");
    } finally {
      setItemsLoading(false);
    }
  };

  const askDelete = (t) => {
    setDetail(null);
    Modal.confirm({
      title: "Supprimer ce transfert ?",
      content: `Le transfert ${t.invoice_number} et ses mouvements de stock seront supprimés.`,
      okText: "Oui, supprimer", okType: "danger", cancelText: "Non", centered: true,
      onOk: async () => {
        try {
          await deleteTransfer(t.id);
          message.success("Transfert supprimé avec succès");
          setPage(1);
          load(1);
        } catch (err) {
          message.error(err.response?.data?.error || "Erreur lors de la suppression du transfert");
        }
      },
    });
  };

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };

  // En « Envoyés » on montre la destination ; en « Reçus », la source.
  const counterpart = (t) =>
    viewMode === "sent" ? t.warehouse_name : t.from_warehouse_name;

  if (!selectedWarehouse) {
    return (
      <div className="body">
        <EmptyState
          icon="warehouse" title="Aucun établissement sélectionné"
          text="Choisissez un établissement dans la barre de navigation pour voir ses transferts."
        />
      </div>
    );
  }

  return (
    <>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <Segmented
          value={viewMode} onChange={setViewMode}
          options={[{ value: "sent", label: "Envoyés" }, { value: "received", label: "Reçus" }]}
        />
        <Summary items={[
          { label: "Transferts", value: fmt(total) },
          { label: "Quantité chargée", value: fmtQty(totalQty) },
        ]} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement des transferts" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="arrow-right" title="Aucun transfert"
            text={viewMode === "sent"
              ? "Cet établissement n'a envoyé aucun transfert."
              : "Cet établissement n'a reçu aucun transfert."}
          />
        ) : (
          <div className="list">
            {rows.map((t) => {
              const p = transferPill(t.order_status);
              return (
                <button key={t.id} className="rcard" onClick={() => openDetail(t)}>
                  <Mono name={t.invoice_number || "T"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{t.invoice_number}</div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">{fmtDate(t.order_date)}</span>
                      <Pill tone={p.tone} label={p.label} />
                      <CountChip icon="boxes" value={Number(t.total_items) || 0} />
                    </div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">
                        {viewMode === "sent" ? "Vers" : "De"} {counterpart(t) || "—"}
                      </span>
                    </div>
                  </div>
                  <span className="rcard__chev"><Ico name="chevron-right" /></span>
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      <Sheet
        open={Boolean(detail)} onClose={() => setDetail(null)}
        title={detail?.invoice_number} subtitle="Détail du transfert"
      >
        {detail ? (
          <>
            <DetailRow label="Date" value={fmtDate(detail.order_date)} />
            <DetailRow label="Établissement source" value={detail.from_warehouse_name || "—"} />
            <DetailRow label="Établissement destination" value={detail.warehouse_name || "—"} />
            <DetailRow label="Statut" value={transferPill(detail.order_status).label} />
            <DetailRow label="Produits" value={fmt(detail.total_items)} />
            <DetailRow label="Quantité totale" value={fmtQty(detail.total_quantity)} />

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Lignes du transfert</div>
            {itemsLoading ? (
              <EmptyState loading title="Chargement des lignes" />
            ) : !items.length ? (
              <Note>Aucune ligne à afficher pour ce transfert.</Note>
            ) : (
              items.map((it, i) => (
                <DetailRow
                  key={it.id ?? i}
                  label={it.product_name}
                  value={`${fmtQty(it.quantity)}${it.subtotal != null ? ` · ${fmt(it.subtotal)} F` : ""}`}
                />
              ))
            )}

            <div style={{ marginTop: 16 }}>
              <Note>La création et la modification d'un transfert se font sur ordinateur.</Note>
            </div>

            {canDelete ? (
              <ActionRow icon="trash-2" label="Supprimer le transfert" danger onClick={() => askDelete(detail)} />
            ) : null}
          </>
        ) : null}
      </Sheet>
    </>
  );
}
